
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UserPresence {
  user_id: string;
  online_at: number;
  status: 'online' | 'away' | 'offline';
}

interface PresenceState {
  [key: string]: UserPresence[];
}

interface PresenceContextType {
  getUserStatus: (userId: string) => 'online' | 'away' | 'offline';
  presenceState: PresenceState;
  updateUserStatus: (status: 'online' | 'away' | 'offline') => Promise<void>;
  isConnected: boolean;
}

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

export const usePresenceContext = () => {
  const context = useContext(PresenceContext);
  if (context === undefined) {
    throw new Error('usePresenceContext must be used within a PresenceProvider');
  }
  return context;
};

export const PresenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [presenceState, setPresenceState] = useState<PresenceState>({});
  const [channel, setChannel] = useState<any>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Initialize global presence tracking
  useEffect(() => {
    if (!user) {
      // If user logs out, clean up presence
      if (channel) {
        console.log('User logged out, cleaning up presence');
        channel.untrack();
        supabase.removeChannel(channel);
        setChannel(null);
        setIsConnected(false);
        setPresenceState({});
      }
      return;
    }

    console.log('Initializing global presence tracking for user:', user.id);
    
    const presenceChannel = supabase.channel('presence:global', {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    // Set up presence event listeners
    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();
        console.log('Global presence sync event - current state:', state);
        
        // Transform Supabase presence state to our format
        const transformedState: PresenceState = {};
        Object.entries(state).forEach(([key, presences]) => {
          transformedState[key] = (presences as any[]).map(presence => ({
            user_id: key,
            online_at: presence.online_at || Date.now(),
            status: presence.status || 'online'
          }));
        });
        
        console.log('Transformed global presence state:', transformedState);
        setPresenceState(transformedState);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('User joined global presence:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('User left global presence:', key, leftPresences);
      })
      .subscribe(async (status) => {
        console.log('Global presence channel subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          // Track user's presence globally
          const userPresence = {
            user_id: user.id,
            online_at: Date.now(),
            status: 'online'
          };
          
          console.log('Tracking global user presence:', userPresence);
          const trackResult = await presenceChannel.track(userPresence);
          console.log('Global track result:', trackResult);
          setIsConnected(true);
        }
      });

    setChannel(presenceChannel);

    // Set up periodic presence updates every 30 seconds
    const presenceInterval = setInterval(async () => {
      if (presenceChannel && user) {
        const userPresence = {
          user_id: user.id,
          online_at: Date.now(),
          status: 'online'
        };
        
        console.log('Updating global presence:', userPresence);
        await presenceChannel.track(userPresence);
      }
    }, 30000);

    // Handle page unload - untrack presence
    const handleBeforeUnload = async () => {
      console.log('Page unloading, untracking presence');
      if (presenceChannel) {
        await presenceChannel.untrack();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      console.log('Cleaning up global presence tracking');
      clearInterval(presenceInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (presenceChannel) {
        presenceChannel.untrack();
        supabase.removeChannel(presenceChannel);
      }
    };
  }, [user]);

  // Get user status from global presence state
  const getUserStatus = useCallback((userId: string): 'online' | 'away' | 'offline' => {
    console.log('Getting global status for user:', userId, 'from presence state:', presenceState);
    
    const userPresences = Object.values(presenceState)
      .flat()
      .filter(presence => presence.user_id === userId);

    console.log('Found global presences for user:', userId, userPresences);

    if (userPresences.length === 0) {
      console.log('No global presence found for user:', userId, '- returning offline');
      return 'offline';
    }

    const latestPresence = userPresences.reduce((latest, current) => 
      current.online_at > latest.online_at ? current : latest
    );

    const timeSinceOnline = Date.now() - latestPresence.online_at;
    const fiveMinutes = 5 * 60 * 1000;
    const oneMinute = 60 * 1000;

    console.log('Latest global presence for user:', userId, latestPresence, 'time since online:', timeSinceOnline);

    if (timeSinceOnline < oneMinute) {
      console.log('User is online globally:', userId);
      return 'online';
    }
    if (timeSinceOnline < fiveMinutes) {
      console.log('User is away globally:', userId);
      return 'away';
    }
    
    console.log('User is offline globally:', userId);
    return 'offline';
  }, [presenceState]);

  // Function to manually update user status
  const updateUserStatus = useCallback(async (status: 'online' | 'away' | 'offline') => {
    if (!user || !channel) return;

    const userPresence = {
      user_id: user.id,
      online_at: Date.now(),
      status: status
    };

    console.log('Manually updating global user status:', userPresence);
    await channel.track(userPresence);
  }, [user, channel]);

  return (
    <PresenceContext.Provider 
      value={{
        getUserStatus,
        presenceState,
        updateUserStatus,
        isConnected
      }}
    >
      {children}
    </PresenceContext.Provider>
  );
};
